import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Button, ConfigProvider, Modal, message, theme as antdTheme } from 'antd';
import { CaretDown, CaretRight, CheckCircle, Copy } from '@phosphor-icons/react';
import miaPlatformDataService from '../../services/MiaPlatformDataService';
import miaFlowAuth from '../../auth/MiaFlowAuth';
import { createQuickVfsProject } from '../../utils/quickProjectCreation';
import { openProject } from '../../utils/openProject';
import * as vfsClient from '../../utils/vfsClient';
import { applyTemplate, getPreviewBlob, getRemixInfo, listTemplates, remixInto } from '../../services/TemplatesService';
import ImportProjectDialog from './ImportProjectDialog';
import StartFromGit from './StartFromGit';
import { startProject } from './projectRun';
import { projectStartKey, runStart } from './startRuns';
import './sandbox.css';
import './NewProjectDialog.css';
import './ImportProjectDialog.css';

export const MAX_NAME_LENGTH = 60;

/** Why a project name cannot be used, or null when it can. */
export function validateProjectName(raw) {
  const name = (raw || '').trim();
  if (!name) { return 'Give your project a name.'; }
  if (name.length > MAX_NAME_LENGTH) { return `Keep it to ${MAX_NAME_LENGTH} characters or fewer.`; }
  if (!/[a-z0-9]/i.test(name)) { return 'Use at least one letter or number.'; }
  return null;
}

/** The tenant a new project goes to: the one passed in, the selected one, or the person's first (resolved silently). */
export async function resolveTenantId(selectedTenant) {
  const known = selectedTenant || miaFlowAuth.getSelectedTenant();
  const knownId = known?.tenantId || known?.id;
  if (knownId) { return knownId; }
  const tenants = (await miaPlatformDataService.getTenants().catch(() => [])) || [];
  const first = tenants.map((tenant) => miaPlatformDataService.formatTenant(tenant))[0];
  if (!first) { return null; }
  miaFlowAuth.setSelectedTenant?.(first);
  return first.tenantId || first.id;
}

/**
 * Start all, in the background, for a project just filled from a template (as a project made from the chat starts):
 * under the project's own start key, so its Home's Start all shows the progress. A start that cannot finish (the plan's
 * limit, a failed build) says so; Start all on the Home tries again.
 */
export function startCreatedProject(projectId, services) {
  const items = (services || []).filter(Boolean).map((serviceName) => ({ serviceName, service: null }));
  if (!items.length) { return Promise.resolve(null); }
  return runStart(projectStartKey(projectId), (onProgress) => startProject(projectId, items, { onProgress }))
    .then((result) => {
      if (result?.error) { message.warning(`The new project did not start: ${result.error} Use Start all on its Home to try again.`, 8); }
      return result;
    })
    .catch(() => null);
}

/** "<name> remix", kept within the name's limit. */
export const remixName = (name) => `${String(name || 'Project').trim().slice(0, MAX_NAME_LENGTH - 6)} remix`;

const GROUPS = [
  { category: 'app', title: 'Starting points', hint: 'A small working app to change into yours' },
  { category: 'starter', title: 'Blank starters', hint: 'The stack set up, nothing built yet' },
];

/** A template's preview (an SVG served by vfs-service, fetched with the person's token), else its initials. */
function TemplatePreview({ template }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!template.preview) { return undefined; }
    let url = null;
    let cancelled = false;
    getPreviewBlob(template.preview)
      .then((blob) => {
        if (cancelled) { return; }
        url = URL.createObjectURL(blob);
        setSrc(url);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (url) { URL.revokeObjectURL(url); }
    };
  }, [template.preview]);
  return src
    ? <img alt="" className="sbx-template-preview" src={src} />
    : <span aria-hidden className="sbx-template-preview is-empty">{String(template.name || '?').slice(0, 2).toUpperCase()}</span>;
}

TemplatePreview.propTypes = { template: PropTypes.object.isRequired };

function TemplateCard({ template, selected, disabled, onPick }) {
  return (
    <button
      aria-pressed={selected}
      className={`sbx-template-card${selected ? ' is-on' : ''}`}
      data-testid="template-card"
      disabled={disabled}
      onClick={() => onPick(template)}
      type="button"
    >
      <TemplatePreview template={template} />
      <span className="sbx-template-body">
        <span className="sbx-template-name">
          {template.name}
          {selected && <CheckCircle aria-label="Selected" className="sbx-template-check" size={16} weight="fill" />}
        </span>
        {template.description && <span className="sbx-template-description">{template.description}</span>}
        {(template.includes || []).length > 0 && (
          <span aria-label="Includes" className="sbx-template-chips">
            {template.includes.map((item) => <span className="sbx-template-chip" key={item}>{item}</span>)}
          </span>
        )}
      </span>
    </button>
  );
}

TemplateCard.propTypes = { disabled: PropTypes.bool, onPick: PropTypes.func.isRequired, selected: PropTypes.bool, template: PropTypes.object.isRequired };

/**
 * "New project" in the sandbox-first experience (docs/SANDBOX_FIRST.md): just a name is the fast path. Creating makes an
 * empty skeleton project in the person's sandbox (vfs-service, via utils/quickProjectCreation.js) and opens it, where
 * they build it with the chat or by hand. "Start from a template" (folded away) shows the gallery from vfs-service:
 * "Starting points" (small working apps) and "Blank starters"; with one picked, the project is created the same way and
 * then filled from the template. "Import a folder or zip" starts one from code the person has instead
 * (ImportProjectDialog); "Start from git" from one or more git repositories, each cloned into its own service
 * (StartFromGit: the project is created first, then each repository is cloned; one that fails can be retried).
 *
 * Remix (`remixOf`: a project the person can see): "Remix <name> by <owner>", what is copied (its services and files,
 * never its data), optionally its seed data; the new project is created, then filled with the copy.
 *
 * `initialTemplateId` / `initialName` come from a link (`/flow/new?template=…&name=…`, NewProjectLink).
 */
export default function NewProjectDialog({ open, onClose, selectedTenant = null, initialTemplateId = null, initialName = null, remixOf = null }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [name, setName] = useState('');
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [importing, setImporting] = useState(false);
  const [fromGit, setFromGit] = useState(false);
  const [gitProject, setGitProject] = useState(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [templates, setTemplates] = useState(null); // null: not loaded
  const [templatesError, setTemplatesError] = useState(null);
  const [templateId, setTemplateId] = useState(null);
  const [templateNote, setTemplateNote] = useState(null);
  const [remix, setRemix] = useState(null); // { info } | { error }
  const [includeSeedData, setIncludeSeedData] = useState(false);

  useEffect(() => {
    if (!open) { setImporting(false); setFromGit(false); setGitProject(null); return undefined; }
    setName(initialName ? String(initialName).slice(0, MAX_NAME_LENGTH) : '');
    setTouched(false);
    setError(null);
    setBusy(false);
    setTemplateId(initialTemplateId || null);
    setTemplateNote(null);
    setGalleryOpen(Boolean(initialTemplateId));
    setRemix(null);
    setIncludeSeedData(false);
    const timer = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(timer);
  }, [open, initialTemplateId, initialName, remixOf]);

  // Remix: what the source project is, then its name ("<name> remix") unless one was given.
  useEffect(() => {
    if (!open || !remixOf) { return undefined; }
    let cancelled = false;
    getRemixInfo(remixOf)
      .then((info) => {
        if (cancelled) { return; }
        setRemix({ info });
        setName((current) => current || remixName(info.name));
      })
      .catch((err) => {
        if (!cancelled) { setRemix({ error: err?.status === 404 || err?.status === 403 ? 'That project cannot be found, or it is not shared with you.' : (err?.message || 'The project to remix could not be loaded.') }); }
      });
    return () => { cancelled = true; };
  }, [open, remixOf]);

  // The gallery, once it is opened (or a link asked for a template).
  useEffect(() => {
    if (!open || remixOf || !galleryOpen || templates) { return undefined; }
    let cancelled = false;
    setTemplatesError(null);
    listTemplates()
      .then((answer) => { if (!cancelled) { setTemplates(answer?.templates || []); } })
      .catch((err) => { if (!cancelled) { setTemplatesError(err?.message || 'The templates could not be loaded.'); setTemplates([]); } });
    return () => { cancelled = true; };
  }, [open, remixOf, galleryOpen, templates]);

  // A template asked for by a link: prefill the name with it, or say it is not there.
  useEffect(() => {
    if (!open || !templates || !initialTemplateId || templatesError) { return; }
    const found = templates.find((t) => t.id === initialTemplateId);
    if (found) {
      setName((current) => current || found.name.slice(0, MAX_NAME_LENGTH));
    } else {
      setTemplateId(null);
      setTemplateNote(`There is no template “${initialTemplateId}” (any more): pick one below, or create an empty project.`);
    }
  }, [open, templates, initialTemplateId, templatesError]);

  const validation = validateProjectName(name);
  const shownError = touched ? validation : null;
  const template = templateId ? (templates || []).find((t) => t.id === templateId) || null : null;
  const remixInfo = remix?.info || null;

  const pick = (picked) => {
    if (picked.id === templateId) { setTemplateId(null); return; }
    setTemplateId(picked.id);
    setTemplateNote(null);
    if (!name.trim()) { setName(String(picked.name || '').slice(0, MAX_NAME_LENGTH)); }
  };

  const registerServices = (projectId, services) => {
    const list = (services || []).filter(Boolean);
    (list.length ? list : ['services/app']).forEach((service) => vfsClient.registerVfsService(projectId, service));
  };

  // Start from git: the project is made the first time StartFromGit clones (it reuses it for every retry).
  const createForGit = async() => {
    setTouched(true);
    if (validation) { return null; }
    const tenantId = await resolveTenantId(selectedTenant);
    if (!tenantId) { throw new Error('Your account has no workspace to create projects in yet.'); }
    const projectName = name.trim();
    const { projectId, name: createdName } = await createQuickVfsProject(tenantId, projectName, null, { registerDefaultService: false });
    setGitProject({ _id: projectId, name: createdName || projectName, tenantId });
    return projectId;
  };
  const gitFinished = ({ projectId, services }) => {
    const created = gitProject || { _id: projectId, name: name.trim() };
    if (!services.length) { registerServices(created._id, []); }
    onClose();
    openProject({ project: created, navigate });
    const count = services.length;
    if (count) { message.success(`Cloned ${count} repositor${count === 1 ? 'y' : 'ies'}. Start ${count === 1 ? 'it' : 'them'} from the Dashboard.`, 5); }
  };

  const submit = async(event) => {
    event?.preventDefault();
    if (fromGit) { return; }
    setTouched(true);
    if (validation || busy || (remixOf && !remixInfo)) { return; }
    setBusy(true);
    setError(null);
    let created = null;
    try {
      const tenantId = await resolveTenantId(selectedTenant);
      if (!tenantId) { throw new Error('Your account has no workspace to create projects in yet.'); }
      const projectName = name.trim();
      const filled = Boolean(remixOf || templateId);
      const { projectId, name: createdName } = filled
        ? await createQuickVfsProject(tenantId, projectName, null, { registerDefaultService: false })
        : await createQuickVfsProject(tenantId, projectName);
      created = { _id: projectId, name: createdName || projectName, tenantId };
    } catch (err) {
      setError(err?.message || 'The project could not be created. Try again.');
      setBusy(false);
      return;
    }
    // The project exists: fill it (template or remix); if that fails, it still opens, with a message.
    let warning = null;
    if (remixOf) {
      try {
        const answer = await remixInto(created._id, remixOf, { includeSeedData: includeSeedData && remixInfo?.seedFiles > 0 });
        registerServices(created._id, answer?.services);
      } catch (err) {
        registerServices(created._id, []);
        warning = `The project was created, but copying ${remixInfo?.name || 'the project'} failed: ${err?.message || 'try the remix again'}.`;
      }
    } else if (templateId) {
      try {
        const answer = await applyTemplate(created._id, templateId);
        registerServices(created._id, answer?.services);
        startCreatedProject(created._id, answer?.services);
      } catch (err) {
        registerServices(created._id, []);
        warning = `The project was created empty: the template ${template?.name || templateId} could not be applied (${err?.message || 'unknown error'}).`;
      }
    }
    onClose();
    openProject({ project: created, navigate });
    if (warning) { message.warning(warning, 8); }
  };

  // antd has no app-wide dark theme here (index.css): follow the page's own data-theme so the dialog matches it.
  const isDark = typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark';
  const wide = !remixOf && !fromGit && galleryOpen;

  return (
    <ConfigProvider theme={{ algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm }}>
      <ImportProjectDialog onClose={onClose} open={open && importing} selectedTenant={selectedTenant} />
      <Modal
        className="sbx-new-project"
        destroyOnHidden
        footer={null}
        mask={{ closable: !busy }}
        onCancel={busy ? undefined : onClose}
        open={open && !importing}
        title={remixOf ? 'Remix a project' : fromGit ? 'New project from git' : 'New project'}
        width={wide ? 760 : fromGit ? 560 : 440}
      >
        <form noValidate onSubmit={submit}>
          {remixOf ? (
            <div className="sbx-remix" data-testid="remix-info">
              {!remix && <p className="sbx-new-project-hint" role="status">Loading the project to remix…</p>}
              {remix?.error && <p className="sbx-new-project-error" role="alert">{remix.error}</p>}
              {remixInfo && (
                <>
                  <p className="sbx-remix-title"><Copy aria-hidden size={16} /> Remix <strong>{remixInfo.name}</strong>{remixInfo.owner?.name || remixInfo.owner?.email ? <> by {remixInfo.owner.name || remixInfo.owner.email}</> : null}</p>
                  <p className="sbx-new-project-hint">
                    A copy of its code in a new project of yours: {(remixInfo.services || []).length > 0
                      ? `${remixInfo.services.length} service${remixInfo.services.length === 1 ? '' : 's'} (${remixInfo.services.map((s) => String(s).replace(/^services\//, '')).join(', ')})`
                      : 'its files'}, {Number(remixInfo.files) || 0} file{Number(remixInfo.files) === 1 ? '' : 's'} in all. Its data is not copied: the datastores of the new project start empty.
                  </p>
                  {remixInfo.seedFiles > 0 && (
                    <label className="sbx-remix-seed">
                      <input checked={includeSeedData} disabled={busy} onChange={(e) => setIncludeSeedData(e.target.checked)} type="checkbox" />
                      Also copy the seed data (sample rows from its db scripts)
                    </label>
                  )}
                </>
              )}
            </div>
          ) : (
            !fromGit && <p className="sbx-new-project-hint">An empty project in your sandbox. You build it in Code, with the chat or by hand.</p>
          )}
          <label className="sbx-new-project-label" htmlFor="sbx-new-project-name">Project name</label>
          <input
            aria-describedby={shownError ? 'sbx-new-project-name-error' : undefined}
            aria-invalid={shownError ? 'true' : 'false'}
            autoComplete="off"
            className={`sbx-new-project-input${shownError ? ' invalid' : ''}`}
            disabled={busy || Boolean(gitProject)}
            id="sbx-new-project-name"
            onBlur={() => { if (name) { setTouched(true); } }}
            onChange={(event) => { setName(event.target.value); setError(null); }}
            placeholder="e.g. Team lunch planner"
            ref={inputRef}
            required
            type="text"
            value={name}
          />
          {shownError && <p className="sbx-new-project-field-error" id="sbx-new-project-name-error">{shownError}</p>}
          {fromGit && (
            <div className="sbx-import-gap">
              <StartFromGit
                ensureProject={createForGit}
                onCancel={() => setFromGit(false)}
                onFinished={gitFinished}
                onSuggestName={(suggested) => setName((current) => current || suggested.slice(0, MAX_NAME_LENGTH))}
                submitLabel="Create and clone"
              />
            </div>
          )}
          {!remixOf && !fromGit && (
            <div className="sbx-templates">
              <button aria-expanded={galleryOpen} className="sbx-templates-toggle" data-testid="templates-toggle" disabled={busy} onClick={() => setGalleryOpen(!galleryOpen)} type="button">
                {galleryOpen ? <CaretDown aria-hidden size={12} /> : <CaretRight aria-hidden size={12} />}
                Start from a template{template ? `: ${template.name}` : ' (optional)'}
              </button>
              {galleryOpen && (
                <div className="sbx-templates-body" data-testid="templates-gallery">
                  {templateNote && <p className="sbx-templates-note" role="status">{templateNote}</p>}
                  {!templates && !templatesError && <p className="sbx-new-project-hint" role="status">Loading the templates…</p>}
                  {templatesError && <p className="sbx-templates-note">{templatesError} You can still create an empty project.</p>}
                  {templates && !templatesError && templates.length === 0 && <p className="sbx-templates-note">No templates are available yet.</p>}
                  {templates && GROUPS.map((group) => {
                    const items = templates.filter((t) => (t.category || 'app') === group.category);
                    if (!items.length) { return null; }
                    return (
                      <section aria-label={group.title} className="sbx-templates-group" key={group.category}>
                        <h3>{group.title}<span>{group.hint}</span></h3>
                        <div className="sbx-templates-grid">
                          {items.map((t) => <TemplateCard disabled={busy} key={t.id} onPick={pick} selected={t.id === templateId} template={t} />)}
                        </div>
                      </section>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {error && <p className="sbx-new-project-error" role="alert">{error}</p>}
          {!remixOf && !fromGit && (
            <p className="sbx-new-project-alt">
              Have code already? <button disabled={busy} onClick={() => setImporting(true)} type="button">Import a folder or zip</button>
              {' or '}<button disabled={busy} onClick={() => setFromGit(true)} type="button">Start from git</button>
            </p>
          )}
          {!fromGit && <div className="sbx-new-project-actions">
            <Button disabled={busy} onClick={onClose}>Cancel</Button>
            <Button disabled={Boolean(remixOf && !remixInfo)} htmlType="submit" loading={busy} type="primary">
              {busy ? 'Creating…' : remixOf ? 'Create the remix' : 'Create project'}
            </Button>
          </div>}
        </form>
      </Modal>
    </ConfigProvider>
  );
}

NewProjectDialog.propTypes = {
  // A link's name for the project (`&name=`).
  initialName: PropTypes.string,
  // The template to start from, preselected (a link's `?template=`); the gallery opens on it.
  initialTemplateId: PropTypes.string,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  // Remix mode: the id of the project to copy.
  remixOf: PropTypes.string,
  selectedTenant: PropTypes.object,
};
